import AsyncStorage from '@react-native-async-storage/async-storage';

export type OwnerDriverIntent = 'yes' | 'no' | null;

export const OWNER_DRIVER_INTENT_STORAGE_KEY = 'ownerAlsoDriverIntent';
export const OWNER_DRIVER_INTENT_EMAIL_STORAGE_KEY = 'ownerAlsoDriverIntentEmail';
export const OWNER_DRIVER_PROMPT_SHOWN_STORAGE_KEY = 'ownerAlsoDriverPromptShown';

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

export function isOwnerDriverIntentBoundToAccount(
  storedEmail: string | null | undefined,
  accountEmail: string | null | undefined,
): boolean {
  return Boolean(
    storedEmail
    && accountEmail
    && normalizeEmail(storedEmail) === normalizeEmail(accountEmail),
  );
}

export async function clearOwnerDriverStorage(): Promise<void> {
  await Promise.all([
    AsyncStorage.removeItem(OWNER_DRIVER_INTENT_STORAGE_KEY),
    AsyncStorage.removeItem(OWNER_DRIVER_INTENT_EMAIL_STORAGE_KEY),
    AsyncStorage.removeItem(OWNER_DRIVER_PROMPT_SHOWN_STORAGE_KEY),
  ]);
}

export async function clearOwnerDriverStorageIfAccountChanged(accountEmail: string): Promise<void> {
  const [storedEmail, storedIntent] = await Promise.all([
    AsyncStorage.getItem(OWNER_DRIVER_INTENT_EMAIL_STORAGE_KEY),
    AsyncStorage.getItem(OWNER_DRIVER_INTENT_STORAGE_KEY),
  ]);

  if (storedIntent && storedEmail && normalizeEmail(storedEmail) === normalizeEmail(accountEmail)) {
    return;
  }

  await clearOwnerDriverStorage();
}

export async function setOwnerDriverIntent(intent: Exclude<OwnerDriverIntent, null>, accountEmail: string): Promise<void> {
  await Promise.all([
    AsyncStorage.setItem(OWNER_DRIVER_INTENT_STORAGE_KEY, intent),
    AsyncStorage.setItem(OWNER_DRIVER_INTENT_EMAIL_STORAGE_KEY, normalizeEmail(accountEmail)),
  ]);
}

export interface OwnerDriverPromptState {
  intent: OwnerDriverIntent;
  hasDriverRole: boolean;
  /** Profile data can be complete before explicit driver activation. */
  hasDriverProfile?: boolean;
  promptAlreadyShown: boolean;
  /** An explicit server value is authoritative over local prompt state. */
  serverIntent?: OwnerDriverIntent;
}

export interface OwnerDriverProfileData {
  nif?: string | null;
  address?: string | null;
  license_number?: string | null;
  date_of_birth?: string | null;
}

export function isOwnerDriverProfileComplete(
  profile: OwnerDriverProfileData | null | undefined,
): boolean {
  if (!profile) return false;
  return [profile.nif, profile.address, profile.license_number, profile.date_of_birth]
    .every((value) => typeof value === 'string' && value.trim().length > 0);
}

export interface OwnerDriverIntentResolutionInput {
  storedIntent: string | null;
  storedEmail: string | null;
  accountEmail: string;
  serverIntent: OwnerDriverIntent;
}

/**
 * Resolve the prompt intent without allowing local storage to override the
 * authenticated account's server value. Local storage remains a fallback for
 * legacy/offline state when the server has no explicit intent.
 */
export function resolveOwnerDriverIntent({
  storedIntent,
  storedEmail,
  accountEmail,
  serverIntent,
}: OwnerDriverIntentResolutionInput): OwnerDriverIntent {
  const localIntent = storedIntent === 'yes' || storedIntent === 'no' ? storedIntent : null;
  const authoritativeServerIntent = serverIntent === 'yes' || serverIntent === 'no'
    ? serverIntent
    : null;
  const localBelongsToAccount = Boolean(
    localIntent
    && storedEmail
    && accountEmail
    && normalizeEmail(storedEmail) === normalizeEmail(accountEmail),
  );
  if (authoritativeServerIntent) return authoritativeServerIntent;
  if (localBelongsToAccount) return localIntent;
  return null;
}

export function shouldShowOwnerDriverPrompt({
  intent,
  hasDriverRole,
  hasDriverProfile = false,
  promptAlreadyShown,
  serverIntent = null,
}: OwnerDriverPromptState): boolean {
  const hasAuthoritativeServerIntent = serverIntent === 'yes' || serverIntent === 'no';
  return intent === 'yes'
    && !hasDriverRole
    && !hasDriverProfile
    && (!promptAlreadyShown || hasAuthoritativeServerIntent);
}

export function shouldShowOwnerDriverCta(hasDriverRole: boolean): boolean {
  return !hasDriverRole;
}
